import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, Users, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const AUDIENCES = [
  ["all", "Everyone signed up"],
  ["citizen", "Citizens"],
  ["client", "Clients"],
  ["dev", "Dev contractors"],
  ["sales", "Sales reps"],
  ["past_client", "Past clients (win-back CRM)"],
];

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [audience, setAudience] = useState("all");
  const [audienceCount, setAudienceCount] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [crmText, setCrmText] = useState("");
  const [crmBusy, setCrmBusy] = useState(false);
  const [crmCount, setCrmCount] = useState(null);

  const loadCrm = useCallback(() => {
    api.get("/admin/marketing/crm/count").then((r) => setCrmCount(r.data)).catch(() => {});
  }, []);

  const importCrm = async () => {
    setCrmBusy(true);
    try {
      const r = await api.post("/admin/marketing/crm/import", { rows: crmText });
      toast.success(`Imported ${r.data.added} contacts (${r.data.skipped} already existed) — ${r.data.total} total`);
      setCrmText("");
      loadCrm();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCrmBusy(false);
    }
  };

  const load = useCallback(() => {
    api.get("/admin/marketing/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadCrm(); }, [load, loadCrm]);

  // live progress: poll while any campaign is mid-send
  const anySending = campaigns.some((c) => c.status === "sending");
  useEffect(() => {
    if (!anySending) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [anySending, load]);

  const resume = async (id) => {
    try {
      const r = await api.post(`/admin/marketing/campaigns/${id}/resume`);
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const retryFailed = async (id) => {
    try {
      const r = await api.post(`/admin/marketing/campaigns/${id}/retry-failed`);
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const duplicate = async (id) => {
    try {
      const r = await api.post(`/admin/marketing/campaigns/${id}/duplicate`);
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    setAudienceCount(null);
    api.get(`/admin/marketing/audience-count?audience=${audience}`).then((r) => setAudienceCount(r.data.count)).catch(() => {});
  }, [audience]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/marketing/campaigns", { subject, body, audience });
      toast.success(r.data.message);
      setSubject(""); setBody(""); setConfirming(false); setPreview(false);
      load();
      setTimeout(load, 4000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-campaigns-section" className="flex flex-col gap-4">
      <div className="border border-white/10 bg-black/40 p-6 flex flex-col gap-3">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2"><Users className="w-3 h-3" /> Win-back CRM — import past clients</p>
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
          Paste a Stripe customer export (CSV text) or a plain list of emails. Duplicates are skipped automatically. These contacts receive the <b className="text-zinc-300">Past clients (win-back CRM)</b> audience campaigns. Emails stay private — never shown publicly.
          {crmCount && <span className="text-nc-blue"> Currently {crmCount.active} active contacts ({crmCount.total} total).</span>}
        </p>
        <textarea
          data-testid="crm-import-input"
          rows={4}
          value={crmText}
          onChange={(e) => setCrmText(e.target.value)}
          placeholder={'Paste CSV text or emails, e.g.\ncustomer_id,created,name,email,gross_volume\ncus_123,...,client@example.com,4200'}
          className={`${inputCls} resize-none text-xs`}
        />
        <button data-testid="crm-import-button" onClick={importCrm} disabled={crmBusy || crmText.trim().length < 3} className="self-start font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white px-5 py-2 transition-colors duration-300">
          {crmBusy ? "Importing…" : "Import Contacts"}
        </button>
      </div>
      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-6 py-4">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-nc-orange" /> Email Campaigns
          </h2>
          <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
            Blast studio news, promotions and release announcements to signed-up users. Every campaign email carries a working unsubscribe link — opted-out users are excluded automatically.
            <span className="text-nc-blue"> Sends are paced to the email channel's capacity (about 10 emails per 5 minutes) — big campaigns deliver gradually, show live progress here, and auto-resume after any restart.</span>
          </p>
        </div>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compose */}
          <div className="border border-white/10 p-4 bg-[#050505]/50 flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><Send className="w-3 h-3" /> Compose Campaign</p>
            <div>
              <label className={labelCls}>Audience</label>
              <select data-testid="campaign-audience-select" className={inputCls} value={audience} onChange={(e) => setAudience(e.target.value)}>
                {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <p data-testid="campaign-audience-count" className="font-mono text-[10px] text-nc-blue mt-1.5 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> {audienceCount == null ? "counting…" : `${audienceCount} recipient${audienceCount === 1 ? "" : "s"}`}
              </p>
            </div>
            <div>
              <label className={labelCls}>Subject *</label>
              <input data-testid="campaign-subject-input" className={inputCls} placeholder="e.g. V1.0 Beta launches this month — plus 20% off environment packs" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={160} />
            </div>
            <div>
              <label className={labelCls}>Message *</label>
              <textarea data-testid="campaign-body-input" rows={8} className={`${inputCls} resize-y leading-relaxed`} placeholder={"Write the announcement…\n\nLine breaks are preserved. Keep it sharp and include a link, e.g. https://specpixel.com/new-citizen"} value={body} onChange={(e) => setBody(e.target.value)} maxLength={20000} />
            </div>
            <div className="flex gap-2">
              <button data-testid="campaign-preview-toggle" onClick={() => setPreview((p) => !p)} className="font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border border-white/15 text-zinc-300 hover:border-white/40 transition-colors flex items-center gap-2">
                <Eye className="w-3.5 h-3.5" /> {preview ? "Edit" : "Preview"}
              </button>
              {!confirming ? (
                <button data-testid="campaign-send-button" disabled={busy || subject.trim().length < 3 || body.trim().length < 10 || !audienceCount} onClick={() => setConfirming(true)} className="flex-1 bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
                  Send to {audienceCount ?? "…"} recipients
                </button>
              ) : (
                <div className="flex-1 flex items-center gap-2 border border-nc-orange/50 bg-nc-orange/10 px-3">
                  <span className="font-mono text-[9px] text-nc-orange uppercase">Blast {audienceCount} inboxes?</span>
                  <button data-testid="campaign-send-confirm" disabled={busy} onClick={send} className="ml-auto font-mono text-[9px] uppercase px-3 py-1.5 bg-nc-orange hover:bg-nc-orange/80 text-black transition-colors">Yes, send</button>
                  <button data-testid="campaign-send-cancel" onClick={() => setConfirming(false)} className="font-mono text-[9px] uppercase px-2 py-1.5 text-zinc-400">No</button>
                </div>
              )}
            </div>
          </div>

          {/* Preview / history */}
          <div className="flex flex-col gap-4">
            {preview && (
              <div data-testid="campaign-preview" className="border border-nc-orange/30 bg-[#0a0a0a] p-5">
                <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase mb-3">SPECPIXEL // STUDIO TRANSMISSION</p>
                <p className="font-sans font-bold text-sm text-zinc-50 mb-3">{subject || "(no subject)"}</p>
                <p className="font-mono text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">{body || "(empty message)"}</p>
                <p className="font-mono text-[9px] text-zinc-600 mt-4 border-t border-white/10 pt-3">Footer includes a working unsubscribe link per recipient.</p>
              </div>
            )}
            <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-2">
              <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase">Campaign History ({campaigns.length})</p>
              {campaigns.length === 0 && <p data-testid="campaigns-empty" className="font-mono text-[10px] text-zinc-600">No campaigns sent yet.</p>}
              {campaigns.map((c, i) => {
                const processed = (c.sent_count || 0) + (c.failed_count || 0);
                const pct = c.recipient_count ? Math.min(100, Math.round((processed / c.recipient_count) * 100)) : 0;
                const chipCls = c.status === "sent"
                  ? "text-nc-green border-nc-green/40 bg-nc-green/10"
                  : c.status === "interrupted"
                    ? "text-red-400 border-red-400/40 bg-red-400/10"
                    : "text-nc-orange border-nc-orange/40 bg-nc-orange/10 animate-pulse";
                return (
                <div key={c.id} data-testid={`campaign-row-${i + 1}`} className="border border-white/10 bg-black/40 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-sans font-semibold text-xs text-zinc-100 truncate">{c.subject}</p>
                    <span data-testid={`campaign-status-${i + 1}`} className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 shrink-0 ${chipCls}`}>{c.status}</span>
                  </div>
                  {c.status === "sending" && (
                    <div data-testid={`campaign-progress-${i + 1}`} className="mt-2">
                      <div className="h-1 bg-white/10 w-full overflow-hidden">
                        <div className="h-full bg-nc-orange transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="font-mono text-[9px] text-nc-orange mt-1">{processed}/{c.recipient_count} processed · {pct}% · live — resumes automatically after any restart</p>
                    </div>
                  )}
                  <p className="font-mono text-[9px] text-zinc-500 mt-1">
                    {c.audience} // {c.sent_count || 0}/{c.recipient_count} delivered{c.failed_count ? ` // ${c.failed_count} failed` : ""} // {String(c.created_at).slice(0, 16).replace("T", " ")}
                  </p>
                  {c.note && <p className="font-mono text-[9px] text-red-400/80 mt-0.5">{c.note}</p>}
                  <div className="flex gap-3 mt-1.5">
                    {c.status === "sending" && (
                      <button data-testid={`campaign-resume-${i + 1}`} onClick={() => resume(c.id)} className="font-mono text-[9px] uppercase tracking-[0.15em] text-nc-orange hover:text-white transition-colors">Resume now</button>
                    )}
                    {c.status === "sent" && (c.failed_count || 0) > 0 && (
                      <button data-testid={`campaign-retry-failed-${i + 1}`} onClick={() => retryFailed(c.id)} className="font-mono text-[9px] uppercase tracking-[0.15em] text-nc-orange hover:text-white transition-colors">Retry {c.failed_count} failed</button>
                    )}
                    {(c.status === "sent" || c.status === "interrupted") && (
                      <button data-testid={`campaign-duplicate-${i + 1}`} onClick={() => duplicate(c.id)} className="font-mono text-[9px] uppercase tracking-[0.15em] text-nc-blue hover:text-white transition-colors">Send again</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
